import { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Paper,
  ButtonGroup,
  Button
} from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import FilterDramaIcon from '@mui/icons-material/FilterDrama';
import NatureIcon from '@mui/icons-material/Nature';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

// Chart components
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  ZAxis,
  RadialBarChart,
  RadialBar
} from 'recharts';

// Import service and types
import { getSeasonalInsights, getOperationalHours, SeasonalInsightsData, OperationalHoursData } from '../services/analytics';
import { getBusinessId } from '../services/business';

// Define the season icons and colors
const seasonIcons = {
  'Winter': <AcUnitIcon />,
  'Spring': <NatureIcon />,
  'Summer': <WbSunnyIcon />,
  'Fall': <FilterDramaIcon />
};

const seasonColors = {
  'Winter': '#41B6E6', // Blue
  'Spring': '#7FD34D', // Green
  'Summer': '#FFC107', // Yellow
  'Fall': '#F57C00'  // Orange
};

const SeasonalInsights = () => {
  const theme = useTheme();
  const businessId = getBusinessId() || 'BIZ001';
  
  // State variables
  const [data, setData] = useState<SeasonalInsightsData | null>(null);
  const [operationalHours, setOperationalHours] = useState<OperationalHoursData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [activeSeason, setActiveSeason] = useState<string>('Summer');
  
  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [insightsData, hoursData] = await Promise.all([
          getSeasonalInsights(businessId),
          getOperationalHours()
        ]);
        setData(insightsData);
        setOperationalHours(hoursData);
        setError(null);
      } catch (err) {
        console.error('Error fetching seasonal insights:', err);
        setError('Failed to load seasonal insights data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [businessId]);
  
  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  // Handle season change
  const handleSeasonChange = (season: string) => {
    setActiveSeason(season);
  };
  
  // Format the heatmap data for the enhanced structure
  const formatHeatmapData = (seasonData: any) => {
    if (!seasonData || !seasonData.weekly_heatmap_data) {
      return {};
    }
    
    // Format for visualization
    const result: { [key: string]: { [key: number]: number } } = {};
    
    Object.keys(seasonData.weekly_heatmap_data).forEach(day => {
      result[day] = {};
      (seasonData.weekly_heatmap_data[day] || []).forEach((hourData: any) => {
        result[day][hourData.hour] = hourData.avg_kwh;
      });
      
      // Fill in missing hours with zeros
      for (let i = 0; i < 24; i++) {
        if (result[day][i] === undefined) {
          result[day][i] = 0;
        }
      }
    });
    
    return result;
  };
  
  // Format legacy heatmap data for backward compatibility
  const formatLegacyHeatmapData = (data: { day: string; hour: number; value: number }[]) => {
    // Days of the week for proper ordering
    const daysOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    // Group by day and hour
    const result: { [key: string]: { [key: number]: number } } = {};
    
    daysOrder.forEach(day => {
      result[day] = {};
      for (let i = 0; i < 24; i++) {
        result[day][i] = 0;
      }
    });
    
    data.forEach(item => {
      result[item.day][item.hour] = item.value;
    });
    
    return result;
  };
  
  // Calculate max value for heatmap
  const getHeatmapMaxValue = (data: { [key: string]: { [key: number]: number } }) => {
    let max = 0;
    Object.keys(data).forEach(day => {
      Object.keys(data[day]).forEach(hour => {
        if (data[day][parseInt(hour)] > max) {
          max = data[day][parseInt(hour)];
        }
      });
    });
    return max;
  };
  
  // Get color for heatmap cell
  const getHeatmapColor = (value: number, max: number) => {
    const intensity = value / max;
    return `rgba(33, 150, 243, ${intensity.toFixed(2)})`;
  };
  
  // Format 24h hour to 12h format with range
  const formatHour = (hour: number) => {
    const startHour = hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`;
    const endHour = (hour + 1) % 24;
    const endFormatted = endHour === 0 ? '12 AM' : endHour < 12 ? `${endHour} AM` : endHour === 12 ? '12 PM' : `${endHour - 12} PM`;
    return `${startHour} - ${endFormatted}`;
  };
  
  // If loading, show a loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // If error, show error message
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body1">{error}</Typography>
        </Alert>
      </Box>
    );
  }
  
  // If no data, show message
  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          <Typography variant="body1">No seasonal insights data available.</Typography>
        </Alert>
      </Box>
    );
  }
  
  // Format pie chart data
  const pieChartData = data.seasonBreakdown.map(item => ({
    name: item.season,
    value: item.kwh,
    percentage: item.percentage
  }));
  
  // Format the heatmap data for the selected season
  const heatmapData = data.weeklyHeatmapBySeason[activeSeason] ? 
    formatHeatmapData(data.weeklyHeatmapBySeason[activeSeason]) : 
    {};
  const heatmapMaxValue = getHeatmapMaxValue(heatmapData);
  
  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Seasonal Energy Insights
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Analyze how your energy usage patterns change throughout the seasons of the year.
        </Typography>
      </Box>
      
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Overview" />
          <Tab label="Season Comparison" />
          <Tab label="Weekly Patterns" />
          <Tab label="Business Hours Analysis" />
          <Tab label="Insights" />
        </Tabs>
      </Box>
      
      {/* Overview Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Seasonal Distribution */}
          <Grid item xs={12} md={5}>
            <Card elevation={0} sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Energy Distribution by Season
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percentage }) => `${name} (${percentage.toFixed(1)}%)`}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={seasonColors[entry.name as keyof typeof seasonColors]} />
                        ))}
                      </Pie>
                      <ChartTooltip formatter={(value: number) => [`${value.toFixed(1)} kWh`, 'Usage']} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  {data.seasonBreakdown.map((season) => (
                    <Grid item xs={6} key={season.season}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        p: 1,
                        borderRadius: 1,
                        bgcolor: alpha(seasonColors[season.season as keyof typeof seasonColors], 0.1)
                      }}>
                        <Box sx={{ 
                          mr: 1, 
                          color: seasonColors[season.season as keyof typeof seasonColors]
                        }}>
                          {seasonIcons[season.season as keyof typeof seasonIcons]}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {season.season}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {season.percentage.toFixed(1)}% of annual usage
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Key Insights */}
          <Grid item xs={12} md={7}>
            <Card elevation={0} sx={{ borderRadius: 2, boxShadow: theme.shadows[2], height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Seasonal Energy Insights
                </Typography>
                
                <List>
                  {data.insights.map((insight, index) => (
                    <ListItem key={index} alignItems="flex-start" sx={{ py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <InfoOutlinedIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={insight} />
                    </ListItem>
                  ))}
                </List>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" gutterBottom>
                  Peak Hours by Season
                </Typography>
                
                <Grid container spacing={2}>
                  {Object.entries(data.peakHoursBySeason).map(([season, peak]) => (
                    <Grid item xs={12} sm={6} key={season}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: alpha(seasonColors[season as keyof typeof seasonColors], 0.1)
                      }}>
                        <Box sx={{ 
                          mr: 1.5, 
                          color: seasonColors[season as keyof typeof seasonColors]
                        }}>
                          {seasonIcons[season as keyof typeof seasonIcons]}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2">
                            {season}
                          </Typography>
                          <Typography variant="body2">
                            Peak at <strong>{peak.hour}:00</strong> ({peak.kwh.toFixed(2)} kWh)
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                
                <Box sx={{ 
                  mt: 3, 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  border: `1px dashed ${theme.palette.warning.main}`
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TipsAndUpdatesIcon sx={{ mr: 1, color: theme.palette.warning.main }} />
                    <Typography variant="subtitle2" color="warning.main">
                      Optimization Tip
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    Consider adjusting your HVAC schedules and equipment usage to account for seasonal variations 
                    in energy demand. Peak reduction strategies may need to be adjusted seasonally.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Season Comparison Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          {/* Peak Hours by Season */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {Object.keys(data?.analysis_by_season || {}).map((season) => {
                const seasonData = data?.analysis_by_season[season];
                if (!seasonData) return null;
                
                return (
                  <Grid item xs={12} sm={6} md={3} key={season}>
                    <Card 
                      elevation={0} 
                      sx={{ 
                        borderRadius: 2, 
                        boxShadow: theme.shadows[2],
                        borderTop: `4px solid ${seasonColors[season as keyof typeof seasonColors]}` 
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ mr: 1, color: seasonColors[season as keyof typeof seasonColors] }}>
                            {seasonIcons[season as keyof typeof seasonIcons]}
                          </Box>
                          <Typography variant="h6">{season}</Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Peak Hour:
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {formatHour(seasonData.peak_hour_info.peak_hour)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Average Usage at Peak:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {seasonData.peak_hour_info.avg_kwh_at_peak.toFixed(2)} kWh/hour
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
          
          {/* Hourly Energy Usage by Season */}
          <Grid item xs={12}>
            <Card elevation={0} sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Hourly Energy Usage by Season
                </Typography>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={data?.hourlyBySeasonData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.1)} />
                      <XAxis 
                        dataKey="time" 
                        scale="band"
                        tickFormatter={(time) => time.slice(0, 2)}
                      />
                      <YAxis />
                      <ChartTooltip 
                        formatter={(value) => [`${Number(value).toFixed(2)} kWh/hour`, 'Hourly Energy Usage']}
                        labelFormatter={(label) => `Time: ${label}`}
                      />
                      <Legend />
                      {Object.keys(seasonColors).map((season) => (
                        <Line
                          key={season}
                          type="monotone"
                          dataKey={season}
                          stroke={seasonColors[season as keyof typeof seasonColors]}
                          activeDot={{ r: 8 }}
                          strokeWidth={2}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                
                {/* Key Observations */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Key Observations
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          bgcolor: alpha(seasonColors['Winter'], 0.1),
                          borderRadius: 2,
                          p: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ mr: 1, color: seasonColors['Winter'] }}>
                            {seasonIcons['Winter']}
                          </Box>
                          <Typography variant="subtitle2">Winter</Typography>
                        </Box>
                        <Typography variant="body2">
                          More constant usage throughout the day with higher evening peak, likely from heating and lighting needs during shorter days.
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          bgcolor: alpha(seasonColors['Spring'], 0.1),
                          borderRadius: 2,
                          p: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ mr: 1, color: seasonColors['Spring'] }}>
                            {seasonIcons['Spring']}
                          </Box>
                          <Typography variant="subtitle2">Spring</Typography>
                        </Box>
                        <Typography variant="body2">
                          Moderate energy needs with less pronounced peaks, reflecting milder weather and balanced heating/cooling requirements.
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          bgcolor: alpha(seasonColors['Summer'], 0.1),
                          borderRadius: 2,
                          p: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ mr: 1, color: seasonColors['Summer'] }}>
                            {seasonIcons['Summer']}
                          </Box>
                          <Typography variant="subtitle2">Summer</Typography>
                        </Box>
                        <Typography variant="body2">
                          Highest overall usage with pronounced afternoon peaks, typically due to air conditioning demand during the hottest hours.
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          bgcolor: alpha(seasonColors['Fall'], 0.1),
                          borderRadius: 2,
                          p: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ mr: 1, color: seasonColors['Fall'] }}>
                            {seasonIcons['Fall']}
                          </Box>
                          <Typography variant="subtitle2">Fall</Typography>
                        </Box>
                        <Typography variant="body2">
                          Transitional pattern with moderate peaks, showing the shift from cooling to heating needs as temperatures decline.
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Average Energy Usage by Season */}
          <Grid item xs={12}>
            <Card elevation={0} sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Average Daily Energy Usage by Season
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={Object.entries(data?.analysis_by_season || {}).map(([season, seasonData]) => ({
                        season,
                        value: seasonData.average_kwh
                      }))}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.1)} />
                      <XAxis dataKey="season" />
                      <YAxis />
                      <ChartTooltip
                        formatter={(value) => [`${Number(value).toFixed(2)} kWh/day`, 'Average Daily Usage']}
                      />
                      <Bar 
                        dataKey="value" 
                        radius={[4, 4, 0, 0]}
                      >
                        {Object.entries(data?.analysis_by_season || {}).map(([season, _], index) => (
                          <Cell key={`cell-${index}`} fill={seasonColors[season as keyof typeof seasonColors]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Weekly Patterns Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          {/* Season Selector */}
          <Grid item xs={12}>
            <Card elevation={0} sx={{ borderRadius: 2, boxShadow: theme.shadows[2], mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <ButtonGroup variant="outlined" size="large">
                  {['Winter', 'Spring', 'Summer', 'Fall'].map((season) => (
                    <Button
                      key={season}
                      onClick={() => handleSeasonChange(season)}
                      sx={{
                        px: 3,
                        backgroundColor: activeSeason === season ? alpha(seasonColors[season as keyof typeof seasonColors], 0.1) : 'transparent',
                        borderColor: seasonColors[season as keyof typeof seasonColors],
                        color: seasonColors[season as keyof typeof seasonColors],
                        '&:hover': {
                          backgroundColor: alpha(seasonColors[season as keyof typeof seasonColors], 0.05),
                          borderColor: seasonColors[season as keyof typeof seasonColors],
                        },
                        ...(activeSeason === season && {
                          borderWidth: 2,
                        })
                      }}
                      startIcon={seasonIcons[season as keyof typeof seasonIcons]}
                    >
                      {season}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
            </Card>
          </Grid>
          
          {/* Heatmap */}
          <Grid item xs={12}>
            <Card elevation={0} sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Weekly Energy Heatmap - {activeSeason}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Hourly average energy usage in kWh/hour by day of week. Data collected since becoming a Green P100NEER (Period: {data?.periodCovered.start} to {data?.periodCovered.end}).
                </Typography>
                <Box sx={{ mt: 2, mb: 4 }}>
                  {data?.analysis_by_season[activeSeason] ? (
                    <>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: '80px repeat(24, 40px)',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 1
                        }}>
                          <Box sx={{ gridColumn: '1 / 2', textAlign: 'center' }}>
                            <Typography variant="subtitle2">Day</Typography>
                          </Box>
                          {Array.from({ length: 24 }, (_, i) => (
                            <Box key={i} sx={{ gridColumn: `${i + 2} / ${i + 3}`, textAlign: 'center' }}>
                              <Typography variant="caption">{i}</Typography>
                            </Box>
                          ))}
                        </Box>
                        
                        {/* Heatmap Grid */}
                        {(() => {
                          // Either use new data structure or fall back to legacy
                          const heatmapData = data.analysis_by_season[activeSeason] 
                            ? formatHeatmapData(data.analysis_by_season[activeSeason])
                            : formatLegacyHeatmapData(data.weeklyHeatmapBySeason[activeSeason] || []);
                          
                          const maxValue = getHeatmapMaxValue(heatmapData);
                          const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                          
                          return (
                            <Box sx={{ minWidth: 1040 }}> {/* Ensure it's wide enough */}
                              {days.map((day, dayIndex) => (
                                <Box key={day} sx={{ 
                                  display: 'grid', 
                                  gridTemplateColumns: '80px repeat(24, 40px)',
                                  borderBottom: dayIndex < days.length - 1 ? '1px solid' : 'none',
                                  borderColor: 'divider',
                                  py: 1
                                }}>
                                  <Box sx={{ 
                                    gridColumn: '1 / 2', 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    pl: 1
                                  }}>
                                    <Typography variant="body2">{day}</Typography>
                                  </Box>
                                  
                                  {Array.from({ length: 24 }, (_, hourIndex) => {
                                    const value = heatmapData[day]?.[hourIndex] || 0;
                                    const color = getHeatmapColor(value, maxValue);
                                    
                                    // Check if this hour is operational
                                    const dayOfWeek = dayIndex === 6 ? 0 : dayIndex + 1; // Convert to Sunday = 0 format
                                    const isDayOpen = operationalHours?.schedule[dayOfWeek.toString()]?.isOpen;
                                    const startHour = isDayOpen ? parseInt(operationalHours?.schedule[dayOfWeek.toString()]?.start?.split(':')[0] || '0') : -1;
                                    const endHour = isDayOpen ? parseInt(operationalHours?.schedule[dayOfWeek.toString()]?.end?.split(':')[0] || '0') : -1;
                                    const isOperational = isDayOpen && hourIndex >= startHour && hourIndex < endHour;
                                    
                                    return (
                                      <Box 
                                        key={hourIndex} 
                                        sx={{ 
                                          gridColumn: `${hourIndex + 2} / ${hourIndex + 3}`,
                                          height: 35,
                                          bgcolor: color,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          position: 'relative',
                                          border: isOperational ? `1px solid ${alpha('#000', 0.2)}` : 'none',
                                          borderRadius: 1
                                        }} 
                                        title={`${day}, ${formatHour(hourIndex)}: ${value.toFixed(2)} kWh/hour`}
                                      >
                                        <Typography 
                                          variant="caption" 
                                          sx={{ 
                                            color: value > maxValue * 0.5 ? '#fff' : '#000', 
                                            fontWeight: 'bold',
                                            fontSize: '0.65rem'
                                          }}
                                        >
                                          {value.toFixed(1)}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ))}
                            </Box>
                          );
                        })()}
                      </Box>
                      
                      {/* Legend */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 3 }}>
                        <Box sx={{ 
                          width: 300, 
                          height: 20, 
                          background: 'linear-gradient(to right, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 1))',
                          borderRadius: 1,
                          mr: 2
                        }} />
                        <Typography variant="caption">
                          Low Energy Usage (kWh/hour) ──────────────────────────── High Energy Usage (kWh/hour)
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Alert severity="info">
                      No heatmap data available for {activeSeason}.
                    </Alert>
                  )}
                </Box>
                
                {/* About Heatmaps */}
                <Box sx={{ mt: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <InfoOutlinedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    About Energy Heatmaps
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This heatmap shows average hourly energy usage patterns (kWh/hour) for each hour of the day across different days of the week. 
                    Darker colors indicate higher energy consumption. Each cell displays the average hourly kWh, with cells showing borders indicating operational hours 
                    based on your business schedule. Each hour represents the interval (e.g., 9 represents 9:00 - 10:00).
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Business Hours Analysis Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          {/* Business Hours vs. Non-Business Hours */}
          <Grid item xs={12}>
            <Card elevation={0} sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Operational vs. Non-Operational Hours Usage
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Analysis of hourly energy usage (kWh/hour) during business and non-business hours. Data period: {data?.periodCovered.start} to {data?.periodCovered.end}.
                </Typography>
                
                {/* Business Hours Schedule */}
                <Box sx={{ mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Business Hours Schedule
                  </Typography>
                  <Grid container spacing={2}>
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => {
                      const dayData = operationalHours?.schedule[index.toString()];
                      const isOpen = dayData?.isOpen;
                      
                      return (
                        <Grid item xs={6} md={3} lg={1.7} key={day}>
                          <Box sx={{ 
                            p: 1, 
                            borderRadius: 1, 
                            border: '1px solid',
                            borderColor: isOpen ? 'primary.main' : 'divider',
                            bgcolor: isOpen ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.grey[500], 0.05),
                            textAlign: 'center'
                          }}>
                            <Typography variant="subtitle2">{day}</Typography>
                            <Typography variant="body2" color={isOpen ? 'primary' : 'text.secondary'}>
                              {isOpen 
                                ? `${dayData?.start} - ${dayData?.end}` 
                                : 'Closed'}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
                
                {/* Grouped Bar Chart */}
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={data?.businessHoursBySeason}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.1)} />
                      <XAxis dataKey="season" />
                      <YAxis />
                      <ChartTooltip 
                        formatter={(value) => [`${Number(value).toFixed(2)} kWh/hour`, 'Average Hourly Usage']}
                      />
                      <Legend />
                      <Bar 
                        name="Business Hours" 
                        dataKey="business" 
                        fill={theme.palette.primary.main} 
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar 
                        name="Non-Business Hours" 
                        dataKey="nonBusiness" 
                        fill={theme.palette.secondary.main} 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                
                {/* Ratio Cards */}
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <CompareArrowsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Business vs. Non-Business Hours Ratio by Season
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(data?.analysis_by_season || {}).map(([season, seasonData]) => (
                      <Grid item xs={12} sm={6} md={3} key={season}>
                        <Card 
                          elevation={0} 
                          sx={{ 
                            borderRadius: 2, 
                            boxShadow: theme.shadows[1],
                            borderLeft: `4px solid ${seasonColors[season as keyof typeof seasonColors]}`,
                            borderColor: theme.palette.divider
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ mr: 1, color: seasonColors[season as keyof typeof seasonColors] }}>
                                {seasonIcons[season as keyof typeof seasonIcons]}
                              </Box>
                              <Typography variant="h6">{season}</Typography>
                            </Box>
                            
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Business Hours
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {seasonData.business_hours_analysis.operational_avg_kwh.toFixed(2)} kWh/hour
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Non-Business
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {seasonData.business_hours_analysis.non_operational_avg_kwh.toFixed(2)} kWh/hour
                                </Typography>
                              </Grid>
                            </Grid>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Typography variant="body2" color="text.secondary">
                              Ratio (Business / Non-Business)
                            </Typography>
                            <Typography 
                              variant="h5" 
                              sx={{ 
                                fontWeight: 700,
                                color: seasonData.business_hours_analysis.ratio > 1 
                                  ? theme.palette.success.main 
                                  : theme.palette.error.main
                              }}
                            >
                              {seasonData.business_hours_analysis.ratio.toFixed(2)}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                
                {/* Analysis Note */}
                <Box sx={{ mt: 3, bgcolor: alpha(theme.palette.info.main, 0.05), p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <InfoOutlinedIcon sx={{ mr: 1, verticalAlign: 'middle', color: theme.palette.info.main }} />
                    A ratio greater than 1.0 indicates higher average hourly energy usage during business hours compared to non-business hours, which is typically expected. 
                    A ratio close to 1.0 may indicate energy waste during non-operational hours, such as equipment left running. 
                    This analysis uses your configured operational hours schedule to define business vs. non-business periods.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Insights Tab */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          {/* Seasonal Energy Insights */}
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ borderRadius: 2, boxShadow: theme.shadows[2], height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <InfoOutlinedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Seasonal Energy Insights
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Key energy usage observations based on hourly aggregated data (kWh/hour).
                </Typography>
                <List>
                  {data?.insights.map((insight, index) => {
                    // Replace the hour format in insights text to show range
                    const updatedInsight = insight.replace(
                      /at (\d+):00 with/g, 
                      (match, hour) => `at ${formatHour(parseInt(hour))} with`
                    );
                    
                    return (
                      <ListItem key={index} sx={{ py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <InfoOutlinedIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={updatedInsight} />
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Optimization Recommendations */}
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ borderRadius: 2, boxShadow: theme.shadows[2], height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <TipsAndUpdatesIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Optimization Recommendations
                </Typography>
                
                {/* Winter Strategies */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ 
                    mb: 1, 
                    color: seasonColors['Winter'],
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {seasonIcons['Winter']}
                    <Box component="span" sx={{ ml: 1 }}>Winter Strategies</Box>
                  </Typography>
                  <List dense disablePadding>
                    <ListItem>
                      <ListItemText 
                        primary="Schedule heating system setbacks during non-operational hours"
                        secondary="Energy savings potential: 10-15%"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Improve insulation to reduce heat loss"
                        secondary="ROI timeline: 2-3 years"
                      />
                    </ListItem>
                  </List>
                </Box>
                
                {/* Summer Strategies */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ 
                    mb: 1, 
                    color: seasonColors['Summer'],
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {seasonIcons['Summer']}
                    <Box component="span" sx={{ ml: 1 }}>Summer Strategies</Box>
                  </Typography>
                  <List dense disablePadding>
                    <ListItem>
                      <ListItemText 
                        primary="Pre-cool the building before peak hours to reduce afternoon HVAC loads"
                        secondary="Peak demand reduction potential: 20-25%"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Install shade devices or window film to reduce solar heat gain"
                        secondary="Energy savings potential: 5-10%"
                      />
                    </ListItem>
                  </List>
                </Box>
                
                {/* Year-Round Strategies */}
                <Box>
                  <Typography variant="subtitle1" sx={{ 
                    mb: 1, 
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <AccessTimeIcon sx={{ mr: 1 }} />
                    Year-Round Strategies
                  </Typography>
                  <List dense disablePadding>
                    <ListItem>
                      <ListItemText 
                        primary="Implement automated shutdown procedures for non-operational hours"
                        secondary="Potential non-operational usage reduction: 30-40%"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Conduct regular seasonal HVAC maintenance"
                        secondary="System efficiency improvement: 15-20%"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Adjust thermostats seasonally to optimize for comfort and efficiency"
                        secondary="Energy savings potential: 5-8% per degree of adjustment"
                      />
                    </ListItem>
                  </List>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Data period info */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Typography variant="caption" color="text.secondary">
          Data period: {data.periodCovered.start} to {data.periodCovered.end}
        </Typography>
      </Box>
    </Container>
  );
};

export default SeasonalInsights; 